import { useEffect, useMemo, useState } from "react";
import { collection, onSnapshot, orderBy, query } from "firebase/firestore";
import { db } from "../config/firebase";
import { useTasks } from "../context/TaskContext";

const normalizeEntryDate = (timestamp) => {
  if (!timestamp) return null;
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  if (Number.isNaN(date.getTime())) return null;
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const formatSummary = (entries) => {
  const map = {};
  entries.forEach((entry) => {
    const key = normalizeEntryDate(entry.currentTime) || normalizeEntryDate(new Date());
    if (!key) return;
    if (!map[key]) {
      map[key] = 0;
    }
    map[key] += 1;
  });
  return map;
};

export const useJournalEntries = () => {
  const { user } = useTasks();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      setEntries([]);
      setLoading(false);
      return;
    }

    const journalsRef = collection(db, `users/${user.uid}/journals`);
    const q = query(journalsRef, orderBy("currentTime", "desc"));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const nextEntries = snapshot.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
      }));
      setEntries(nextEntries);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [user]);

  const entriesByDate = useMemo(() => {
    const grouped = {};
    entries.forEach((entry) => {
      const key = normalizeEntryDate(entry.currentTime);
      if (!key) return;
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(entry);
    });
    return grouped;
  }, [entries]);

  const summaries = useMemo(() => formatSummary(entries), [entries]);

  return {
    entries,
    entriesByDate,
    summaries,
    loading,
  };
};
